import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import CabinBar       from "../components/CabinBar";
import ConfigToolbar  from "../components/ConfigToolbar";
import EventCatalog   from "../components/EventCatalog";
import FleetSVG       from "../components/FleetSVG";
import Sidebar        from "../components/Sidebar";
import SiteStrip      from "../components/SiteStrip";
import { buildFleetGeometry, type CabinSection } from "../lib/geometry";
import {
  decodeSelection, encodeSelection, isValidSelection,
  parentSelection, sameSelection,
} from "../lib/selection";
import type { Selection } from "../lib/selection";
import type { AvailMode, ViewMode } from "../lib/viewMode";
import type { TripSeat } from "../lib/types/demo";
import { useAvailability, useCatalog, useEvents, useTopology } from "../lib/api";


// .p-4 on the LOPA scroll container — SVG sits 16px below the pane's top.
const LOPA_TOP_PADDING = 16;
// Focal line where we decide "which cabin am I in" — measured from the pane top.
const FOCAL_OFFSET = 100;


// Key the page by variant so switching fleets remounts it — resetting
// selection, view mode, scroll-spy cabin, and scroll position. Tail changes
// within a fleet (only ?code= changes) keep the mounted state.
export default function FleetPage() {
  const { variantId } = useParams();
  if (!variantId) return null;
  return <FleetView key={variantId} variantId={variantId} />;
}


function FleetView({ variantId }: { variantId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const codeParam = searchParams.get("code") || undefined;
  const topology = useTopology(variantId);
  const catalog = useCatalog(variantId);
  const availability = useAvailability(variantId);
  const events = useEvents(variantId);
  const [currentCabin, setCurrentCabin] = useState<string>("");
  const [eventsOpen, setEventsOpen] = useState(false);
  // Pre-filled search when the catalog is opened from a specific event
  // (Site view's "top events" rows).
  const [eventsQuery, setEventsQuery] = useState<string | undefined>(undefined);

  // Availability demo data: per-site (tail) 30-trip histories. The active
  // site and trip live in the URL (?st= / ?trip=) so trip navigation is
  // back/forward-retraceable and shareable like everything else.
  const sites = availability.data?.sites ?? [];
  const stParam = searchParams.get("st");
  const bundle = sites.find((s) => s.tail === stParam) ?? sites[0];
  const trips = bundle?.trips ?? [];
  const tripParam = searchParams.get("trip");
  // Default = the latest trip in the window.
  const trip = trips.find((t) => t.id === tripParam) ?? trips[trips.length - 1];
  const site = bundle?.site;

  // ---- URL = source of truth for selection / view / sub-mode -------------
  // Every change PUSHES a history entry, so browser back/forward (and the
  // toolbar ◀ ▶ buttons) retrace the interaction trail, reloads restore the
  // exact state, and any state doubles as a shareable link. What-if
  // simulation state is deliberately local — checkbox churn in history
  // would make Back feel noisy.
  const isEx3 = topology.data?.layout.topology.type === "nc-port";
  const rawView = searchParams.get("view");
  const viewMode: ViewMode =
    rawView === "mcu" && !isEx3 ? "mcu" :
    rawView === "avail" ? "avail" : "data";
  const availMode: AvailMode = searchParams.get("sub") === "site" ? "site" : "trip";
  const selection = useMemo<Selection | undefined>(() => {
    if (!topology.data) return undefined;
    const s = decodeSelection(searchParams.get("sel"));
    if (!s || !isValidSelection(s, topology.data)) return undefined;
    // Incident indexes are bound to the loaded trip.
    if (s.kind === "incident" && (!trip || s.idx >= trip.incidents.length)) {
      return undefined;
    }
    return s;
  }, [searchParams, topology.data, trip]);

  // Coalesced push: no-op interactions (re-clicking the same seat/view)
  // don't spam history.
  const pushParams = (mutate: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next);  // default navigate = push
  };
  const setSelection = (s: Selection | undefined) => {
    pushParams((p) => {
      if (s) p.set("sel", encodeSelection(s));
      else p.delete("sel");
    });
  };
  const setViewMode = (v: ViewMode) => {
    pushParams((p) => {
      if (v === "data") p.delete("view");
      else p.set("view", v);
      // Only seat and AI selections exist as a complete, connected path in
      // BOTH the Data and MCU views — anything else (FS, ADB, ports, zones)
      // would leave a disconnected half-highlight after a switch across MCU.
      // Data ↔ Availability share the same head-end tree, so selections
      // survive that switch — except trip incidents, which belong to the
      // availability layer. Folded into the same push as the view change so
      // Back undoes both together.
      const sel = decodeSelection(p.get("sel"));
      if (!sel) return;
      const crossesMcu = v === "mcu" || viewMode === "mcu";
      if ((crossesMcu && sel.kind !== "seat" && sel.kind !== "ai") ||
          (sel.kind === "incident" && v !== "avail")) {
        p.delete("sel");
      }
    });
  };
  const setAvailMode = (m: AvailMode) => {
    pushParams((p) => {
      if (m === "site") p.set("sub", "site");
      else p.delete("sub");
      // Incidents (and their highlights) belong to the trip layer.
      if (m === "site" && decodeSelection(p.get("sel"))?.kind === "incident") {
        p.delete("sel");
      }
    });
  };
  // Deep-dive into a trip (trend bar / worst-trip row / selector): jumps to
  // the trip layer, dropping any incident selection from the previous trip.
  const setTripId = (id: string) => {
    pushParams((p) => {
      p.set("trip", id);
      p.delete("sub");
      if (decodeSelection(p.get("sel"))?.kind === "incident") p.delete("sel");
    });
  };
  const setSiteTail = (tail: string) => {
    pushParams((p) => {
      p.set("st", tail);
      p.delete("trip");  // fresh site opens on its latest trip
      if (decodeSelection(p.get("sel"))?.kind === "incident") p.delete("sel");
    });
  };

  // What-if simulation: exclude incidents fleet-wide, or single events on a
  // single seat, and recompute availability live as if they never happened.
  const [excludedIncidents, setExcludedIncidents] = useState<Set<number>>(new Set());
  const [excludedBySeat, setExcludedBySeat] =
    useState<Map<string, Set<number>>>(new Map());
  const simActive = excludedIncidents.size > 0 || excludedBySeat.size > 0;
  const resetSim = () => {
    setExcludedIncidents(new Set());
    setExcludedBySeat(new Map());
  };
  // Exclusion indexes are per-trip — navigating to another trip (or site)
  // must drop the simulation or it would exclude the wrong incidents.
  const lastTripRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (trip?.id !== lastTripRef.current) {
      lastTripRef.current = trip?.id;
      setExcludedIncidents(new Set());
      setExcludedBySeat(new Map());
    }
  }, [trip?.id]);
  const toggleIncident = (idx: number) => {
    setExcludedIncidents((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };
  const toggleSeatEvent = (seatId: string, idx: number) => {
    setExcludedBySeat((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(seatId) ?? []);
      if (set.has(idx)) set.delete(idx);
      else set.add(idx);
      if (set.size === 0) next.delete(seatId);
      else next.set(seatId, set);
      return next;
    });
  };

  // Simulated trip: same incidents list, seat scores recomputed from the
  // non-excluded events — pct = 100·(1 − Σ(duration·penalty)/total_minutes),
  // matching the generator's availability math. Untouched when idle.
  const simTrip = useMemo(() => {
    if (!trip || !simActive) return trip;
    const seats: typeof trip.seats = {};
    let sum = 0;
    let n = 0;
    for (const [sid, s] of Object.entries(trip.seats)) {
      const total = s.total_minutes ?? 0;
      let penMin = 0;
      if (total > 0) {
        const perSeat = excludedBySeat.get(sid);
        for (const e of s.events) {
          const idx = e.incident_idx;
          const excluded = idx !== null && idx !== undefined &&
            (excludedIncidents.has(idx) || (perSeat?.has(idx) ?? false));
          if (!excluded) penMin += e.duration * e.penalty;
        }
      }
      const pct = total === 0
        ? 100
        : Math.max(0, Math.round(100 * (1 - penMin / total)));
      seats[sid] = { ...s, availability_pct: pct };
      sum += pct;
      n++;
    }
    return {
      ...trip,
      seats,
      avg_score: n > 0 ? Math.round((sum / n) * 10) / 10 : trip.avg_score,
    };
  }, [trip, simActive, excludedIncidents, excludedBySeat]);

  // Incident selection lights up exactly the seats it penalised.
  const brightSeats = useMemo(() => {
    if (selection?.kind !== "incident" || !trip) return null;
    const inc = trip.incidents[selection.idx];
    return inc ? new Set(inc.seats) : null;
  }, [selection, trip]);

  // Site sub-mode recolours seats by their window-average score.
  const siteSeats = useMemo<Record<string, TripSeat> | undefined>(() => {
    if (!site) return undefined;
    const out: Record<string, TripSeat> = {};
    for (const [sid, pct] of Object.entries(site.seatAvgScore)) {
      out[sid] = { availability_pct: pct, events: [] };
    }
    return out;
  }, [site]);

  // Single click = highlight in place; DOUBLE click = highlight + jump the
  // LOPA to the highlighted seat block. The token bump is what triggers the
  // scroll effect in FleetSVG (the two single-clicks of a double-click have
  // already set the selection by the time dblclick fires).
  const [jumpToken, setJumpToken] = useState(0);
  const activate = (s: Selection) => {
    setSelection(s);
    setJumpToken((t) => t + 1);
  };

  // Click-again steps UP the chain instead of clearing: re-clicking the
  // selected FS moves the selection to its parent AI (both FSes + all
  // downstream light up); re-clicking a selected chain port widens to its
  // ADB, and so on up to the root (which clears). A double-click's second
  // click may briefly step up, but the dblclick handler re-asserts the
  // clicked element, so the net result stays "select + jump".
  const select = (s: Selection | undefined) => {
    if (s && selection && topology.data && sameSelection(s, selection)) {
      setSelection(parentSelection(s, topology.data,
        viewMode === "mcu" ? "mcu" : "data"));
    } else {
      setSelection(s);
      // Incidents are picked from the availability panel, off-canvas —
      // jump the LOPA to the penalised seat block so the click lands
      // somewhere visible (single in-canvas clicks never scroll).
      if (s?.kind === "incident") setJumpToken((t) => t + 1);
    }
  };

  // Right-panel width — drag the divider to resize; persisted.
  const [panelW, setPanelW] = useState<number>(() => {
    const v = Number(localStorage.getItem("lopa-panel-w"));
    return v >= 280 && v <= 720 ? v : 420;
  });
  const dragState = useRef<{ startX: number; startW: number } | null>(null);
  const onDividerDown: React.MouseEventHandler = (e) => {
    e.preventDefault();
    dragState.current = { startX: e.clientX, startW: panelW };
    const onMove = (ev: MouseEvent) => {
      const d = dragState.current;
      if (!d) return;
      const next = Math.min(720, Math.max(280, d.startW + (d.startX - ev.clientX)));
      setPanelW(next);
    };
    const onUp = () => {
      dragState.current = null;
      setPanelW((w) => {
        localStorage.setItem("lopa-panel-w", String(w));
        return w;
      });
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const cabinBarLettersRef = useRef<HTMLDivElement>(null);

  const geometry = useMemo(
    () => (topology.data ? buildFleetGeometry(topology.data, viewMode) : null),
    [topology.data, viewMode],
  );

  // Fit-to-width: the SVG scales to the pane, so cabin-bar letters and the
  // scroll-spy need the effective render scale. Measure the RENDERED SVG
  // rather than deriving from clientWidth — computed widths drifted from the
  // browser's actual layout (scrollbar/padding subtleties), leaving the
  // cabin-bar letters misaligned with the seat columns.
  const lopaPaneRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState(1);
  const totalW = geometry?.totalWidth ?? 0;
  // `ready` must be a dependency: topology can resolve before catalog, in
  // which case this effect fires while the page still shows the loading
  // screen — no pane to observe — and totalW alone never re-triggers it.
  // The cabin bar then sticks at scale 1 (the misaligned-letters bug).
  const ready = Boolean(topology.data && catalog.data);
  useEffect(() => {
    const el = lopaPaneRef.current;
    if (!el || totalW <= 0) return;
    const update = () => {
      const svg = el.querySelector("svg");
      const w = svg?.getBoundingClientRect().width ?? 0;
      if (w > 0) setFitScale(w / totalW);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const svg = el.querySelector("svg");
    if (svg) ro.observe(svg);
    return () => ro.disconnect();
  }, [totalW, ready]);

  const onLopaScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    if (cabinBarLettersRef.current) {
      cabinBarLettersRef.current.style.transform =
        `translate3d(${-e.currentTarget.scrollLeft}px, 0, 0)`;
    }
    if (geometry && fitScale > 0) {
      const next = pickCurrentCabin(
        e.currentTarget.scrollTop,
        geometry.sections,
        fitScale,
      );
      if (next && next !== currentCabin) setCurrentCabin(next);
    }
  };

  if (topology.isLoading || catalog.isLoading) {
    return <p className="p-6 text-slate-500">Loading fleet {variantId}…</p>;
  }
  if (topology.isError) {
    return (
      <div className="m-6 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-900">
        Topology error: {(topology.error as Error).message}
      </div>
    );
  }
  if (catalog.isError) {
    return (
      <div className="m-6 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-900">
        Catalog error: {(catalog.error as Error).message}
      </div>
    );
  }

  const t = topology.data!;
  const c = catalog.data!;
  const cabinLabel = currentCabin || geometry?.sections[0]?.cabinName || "";

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col">
      <EventCatalog
        open={eventsOpen}
        onClose={() => { setEventsOpen(false); setEventsQuery(undefined); }}
        events={events.data}
        fleetLabel={t.fleet_label}
        initialQuery={eventsQuery}
      />
      <ConfigToolbar
        topology={t}
        selection={selection}
        onSelect={select}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        availMode={availMode}
        onAvailModeChange={setAvailMode}
        hasSite={Boolean(site)}
        sites={sites}
        siteTail={bundle?.tail}
        onSiteChange={setSiteTail}
        trips={trips}
        tripId={trip?.id}
        onTripChange={setTripId}
        onOpenEvents={() => setEventsOpen(true)}
      />
      <SiteStrip topology={t} code={codeParam} />
      <CabinBar
        topology={t}
        ref={cabinBarLettersRef}
        currentCabin={cabinLabel}
        scale={fitScale}
        spacerW={panelW + 6}
        viewBoxX={geometry?.viewBoxX}
      />
      <div className="flex min-h-0 min-w-0 flex-1">
        <div
          ref={lopaPaneRef}
          onScroll={onLopaScroll}
          className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-gradient-to-b from-[#FAFBFD] to-[#F1F5F9] p-4"
        >
          <FleetSVG
            topology={t}
            selection={selection}
            onSelect={select}
            onActivate={activate}
            jumpToken={jumpToken}
            viewMode={viewMode}
            fitWidth
            avail={
              viewMode !== "avail"
                ? undefined
                : availMode === "site"
                  ? siteSeats
                  : simTrip?.seats
            }
            brightSeats={brightSeats}
          />
        </div>
        {/* Drag divider — resize the right panel. */}
        <div
          onMouseDown={onDividerDown}
          title="Drag to resize"
          className="w-1.5 shrink-0 cursor-col-resize bg-slate-200 transition-colors hover:bg-sky-400"
        />
        <Sidebar
          topology={t}
          catalog={c}
          selection={selection}
          onClose={() => setSelection(undefined)}
          onSelect={select}
          viewMode={viewMode}
          width={panelW}
          availTrip={viewMode === "avail" || selection?.kind === "incident" ? simTrip : undefined}
          availMode={availMode}
          site={viewMode === "avail" ? site : undefined}
          events={events.data}
          onOpenCatalog={(q?: string) => { setEventsQuery(q); setEventsOpen(true); }}
          tripNav={trip && trips.length > 0 ? {
            index: trips.findIndex((x) => x.id === trip.id),
            count: trips.length,
            onPrev: () => {
              const i = trips.findIndex((x) => x.id === trip.id);
              if (i > 0) setTripId(trips[i - 1].id);
            },
            onNext: () => {
              const i = trips.findIndex((x) => x.id === trip.id);
              if (i >= 0 && i < trips.length - 1) setTripId(trips[i + 1].id);
            },
            onBackToSite: () => setAvailMode("site"),
          } : undefined}
          onTripSelect={setTripId}
          whatIf={trip ? {
            active: simActive,
            excluded: excludedIncidents,
            excludedBySeat,
            onToggleIncident: toggleIncident,
            onToggleSeatEvent: toggleSeatEvent,
            onReset: resetSim,
            actualSeats: trip.seats,
          } : undefined}
        />
      </div>
    </div>
  );
}


// Pick the cabin whose banner has scrolled at or above the focal line.
// SVG y = scrollTop - LOPA_TOP_PADDING at the pane's visible top, so a banner
// at the focal line (FOCAL_OFFSET below the pane's top) has SVG y =
// scrollTop - LOPA_TOP_PADDING + FOCAL_OFFSET.
function pickCurrentCabin(
  scrollTop: number,
  sections: CabinSection[],
  scale = 1,
): string | null {
  if (sections.length === 0) return null;
  // Screen-space cutoff → content-space (sections' bannerY is unscaled).
  const cutoff = (scrollTop - LOPA_TOP_PADDING + FOCAL_OFFSET) / scale;
  let current = sections[0].cabinName;
  for (const s of sections) {
    if (s.bannerY <= cutoff) current = s.cabinName;
    else break;
  }
  return current;
}
